(() => {
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  const initializeCarousel = (carousel) => {
    if (carousel.dataset.carouselInitialized === 'true') return;
    carousel.dataset.carouselInitialized = 'true';

    const slides = Array.from(carousel.querySelectorAll('[data-hero-carousel-slide]'));
    const controls = carousel.querySelector('[data-hero-carousel-controls]');
    const previousButton = carousel.querySelector('[data-hero-carousel-previous]');
    const nextButton = carousel.querySelector('[data-hero-carousel-next]');
    const pauseButton = carousel.querySelector('[data-hero-carousel-pause]');
    const currentCounter = carousel.querySelector('[data-hero-carousel-current]');
    const progress = carousel.querySelector('[data-hero-carousel-progress]');
    const stage = carousel.querySelector('[data-hero-carousel-stage]');

    if (slides.length <= 1 || !stage) return;

    let activeIndex = Math.max(0, slides.findIndex((slide) => slide.classList.contains('is-active')));
    let autoplayTimer = null;
    let pointerStartX = null;
    let pointerStartY = null;
    let hovered = false;
    let focused = false;
    let pausedByUser = false;
    const autoplayEnabled = carousel.dataset.autoplay === 'true';
    const pauseOnHover = carousel.dataset.pauseOnHover === 'true';
    const autoplayInterval = Math.max(5000, Number(carousel.dataset.autoplayInterval) || 7000);

    slides.forEach((slide) => {
      slide.hidden = false;
    });
    if (controls) controls.hidden = false;

    const setFocusableState = (slide, active) => {
      slide.toggleAttribute('inert', !active);
      slide.querySelectorAll('a, button, input, select, textarea, [tabindex]').forEach((element) => {
        if (!active) {
          if (!element.hasAttribute('data-hero-original-tabindex')) {
            element.setAttribute('data-hero-original-tabindex', element.getAttribute('tabindex') || '');
          }
          element.setAttribute('tabindex', '-1');
          return;
        }

        if (element.hasAttribute('data-hero-original-tabindex')) {
          const originalTabindex = element.getAttribute('data-hero-original-tabindex');
          if (originalTabindex) {
            element.setAttribute('tabindex', originalTabindex);
          } else {
            element.removeAttribute('tabindex');
          }
          element.removeAttribute('data-hero-original-tabindex');
        }
      });
    };

    const render = (nextIndex) => {
      activeIndex = (nextIndex + slides.length) % slides.length;

      slides.forEach((slide, index) => {
        const active = index === activeIndex;
        slide.classList.toggle('is-active', active);
        slide.setAttribute('aria-hidden', active ? 'false' : 'true');
        setFocusableState(slide, active);
      });
      if (currentCounter) currentCounter.textContent = String(activeIndex + 1).padStart(2, '0');
      if (progress) progress.style.transform = 'scaleX(' + ((activeIndex + 1) / slides.length) + ')';
    };

    const stopAutoplay = () => {
      if (autoplayTimer) window.clearTimeout(autoplayTimer);
      autoplayTimer = null;
    };

    const canAutoplay = () => (
      autoplayEnabled
      && !reducedMotion.matches
      && !document.hidden
      && !focused
      && !pausedByUser
      && !(pauseOnHover && hovered)
    );

    const scheduleAutoplay = () => {
      stopAutoplay();
      if (!canAutoplay()) return;
      autoplayTimer = window.setTimeout(() => {
        render(activeIndex + 1);
        scheduleAutoplay();
      }, autoplayInterval);
    };

    const selectManually = (nextIndex) => {
      render(nextIndex);
      scheduleAutoplay();
    };

    previousButton?.addEventListener('click', () => selectManually(activeIndex - 1));
    nextButton?.addEventListener('click', () => selectManually(activeIndex + 1));
    pauseButton?.addEventListener('click', () => {
      pausedByUser = !pausedByUser;
      pauseButton.setAttribute('aria-pressed', String(pausedByUser));
      pauseButton.setAttribute('aria-label', pausedByUser ? 'Wiedergabe fortsetzen' : 'Wiedergabe anhalten');
      pauseButton.classList.toggle('is-paused', pausedByUser);
      if (pausedByUser) {
        stopAutoplay();
      } else {
        scheduleAutoplay();
      }
    });

    if (pauseOnHover) {
      carousel.addEventListener('mouseenter', () => {
        hovered = true;
        stopAutoplay();
      });
      carousel.addEventListener('mouseleave', () => {
        hovered = false;
        scheduleAutoplay();
      });
    }

    carousel.addEventListener('focusin', () => {
      focused = true;
      stopAutoplay();
    });
    carousel.addEventListener('focusout', () => {
      window.setTimeout(() => {
        focused = carousel.contains(document.activeElement);
        scheduleAutoplay();
      }, 0);
    });

    document.addEventListener('visibilitychange', () => {
      if (document.hidden) {
        stopAutoplay();
      } else {
        scheduleAutoplay();
      }
    });

    const handleReducedMotionChange = () => scheduleAutoplay();
    if (typeof reducedMotion.addEventListener === 'function') {
      reducedMotion.addEventListener('change', handleReducedMotionChange);
    } else {
      reducedMotion.addListener(handleReducedMotionChange);
    }

    stage.addEventListener('pointerdown', (event) => {
      if (event.pointerType !== 'touch' || !event.isPrimary) return;
      pointerStartX = event.clientX;
      pointerStartY = event.clientY;
    });

    stage.addEventListener('pointerup', (event) => {
      if (event.pointerType !== 'touch' || pointerStartX === null || pointerStartY === null) return;
      const distanceX = event.clientX - pointerStartX;
      const distanceY = event.clientY - pointerStartY;
      pointerStartX = null;
      pointerStartY = null;

      if (Math.abs(distanceX) < 40 || Math.abs(distanceX) <= Math.abs(distanceY)) return;
      selectManually(distanceX < 0 ? activeIndex + 1 : activeIndex - 1);
    });

    stage.addEventListener('pointercancel', () => {
      pointerStartX = null;
      pointerStartY = null;
    });

    render(activeIndex);
    scheduleAutoplay();
  };

  const initializeCarousels = (scope = document) => {
    if (scope.matches?.('[data-hero-carousel]')) initializeCarousel(scope);
    scope.querySelectorAll?.('[data-hero-carousel]').forEach(initializeCarousel);
  };

  initializeCarousels();
  document.addEventListener('shopify:section:load', (event) => initializeCarousels(event.target));
})();
